import { ProgrammeStatusType, ProgrammeType } from '../../types';

const STATUS_CLASS_MAP: Record<ProgrammeStatusType, string> = {
    [ProgrammeStatusType.ARCHIVED]: 'archived',
    [ProgrammeStatusType.DRAFT]: 'draft',
    [ProgrammeStatusType.LIVE]: 'live',
    [ProgrammeStatusType.TESTING]: 'testing',
};

const CLASS_DEFINITIONS = [
    'classDef archived fill:#f5f5f5,stroke:#616161,color:#111,stroke-width:1px;',
    'classDef draft fill:#fff8e1,stroke:#ef6c00,color:#111,stroke-width:1px;',
    'classDef live fill:#e8f5e9,stroke:#2e7d32,color:#111,stroke-width:1px;',
    'classDef testing fill:#e3f2fd,stroke:#1565c0,color:#111,stroke-width:1px;',
    'classDef default fill:#ffffff,stroke:#555555,color:#111,stroke-width:1px;',
].join('\n');

export const getVisibleProgrammes = ({
    liveOnly,
    programmes,
    showOrphaned,
}: {
    liveOnly: boolean;
    programmes?: ProgrammeType[];
    showOrphaned: boolean;
}) => {
    if (!programmes) {
        return [];
    }

    const filteredProgrammes = liveOnly
        ? getLiveProgrammeChains(programmes)
        : programmes;

    if (showOrphaned) {
        return filteredProgrammes;
    }

    const idSet = new Set(filteredProgrammes.map((programme) => programme.id));
    const connectedIds = new Set<number>();

    filteredProgrammes.forEach((programme) => {
        if (
            programme.next_programme_id != null &&
            idSet.has(programme.next_programme_id)
        ) {
            connectedIds.add(programme.id);
            connectedIds.add(programme.next_programme_id);
        }
    });

    return filteredProgrammes.filter((programme) =>
        connectedIds.has(programme.id),
    );
};

export const buildProgrammeConnectionsDiagrams = (
    programmes: ProgrammeType[],
) => {
    const programmeMap = new Map(
        programmes.map((programme) => [programme.id, programme]),
    );
    return getConnectedComponents(programmes).map((component) =>
        buildComponentDiagram(component, programmeMap),
    );
};

const buildComponentDiagram = (
    programmes: ProgrammeType[],
    programmeMap: Map<number, ProgrammeType>,
) => {
    const componentIds = new Set(programmes.map((programme) => programme.id));
    const orderedProgrammes = orderComponentProgrammes(
        programmes,
        programmeMap,
    );
    const nodeDefinitions = orderedProgrammes.map(buildNodeDefinition);
    const edgeDefinitions = orderedProgrammes
        .filter(
            (
                programme,
            ): programme is ProgrammeType & { next_programme_id: number } =>
                programme.next_programme_id != null &&
                componentIds.has(programme.next_programme_id),
        )
        .map(
            (programme) =>
                `${getProgrammeNodeId(programme.id)} --> ${getProgrammeNodeId(
                    programme.next_programme_id,
                )}`,
        );

    return [
        'flowchart LR',
        CLASS_DEFINITIONS,
        ...nodeDefinitions,
        ...edgeDefinitions,
    ].join('\n');
};

const buildNodeDefinition = (programme: ProgrammeType) => {
    const programmeId = getProgrammeNodeId(programme.id);
    const yearLabel = programme.year_id
        ? `${'key_stage' in programme ? 'Year' : 'Grade'} ${programme.year_id}`
        : null;
    const labelLines = [
        `#${programme.id}`,
        `<b>${escapeMermaidLabel(programme.title)}</b>`,
        yearLabel,
        programme.status ? escapeMermaidLabel(programme.status) : null,
    ].filter(Boolean);

    const nodeDefinition = `${programmeId}["${labelLines.join('<br/>')}"]`;
    const className = programme.status
        ? STATUS_CLASS_MAP[programme.status]
        : 'default';

    return `${nodeDefinition}\nclass ${programmeId} ${className};`;
};

const escapeMermaidLabel = (value: string) =>
    value
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

export const getProgrammeNodeId = (programmeId: number) =>
    `programme_${programmeId}`;

const getConnectedComponents = (programmes: ProgrammeType[]) => {
    const programmeMap = new Map(
        programmes.map((programme) => [programme.id, programme]),
    );
    const adjacencyMap = new Map<number, Set<number>>();

    programmes.forEach((programme) => {
        adjacencyMap.set(programme.id, new Set<number>());
    });

    programmes.forEach((programme) => {
        if (
            programme.next_programme_id == null ||
            !programmeMap.has(programme.next_programme_id)
        ) {
            return;
        }

        adjacencyMap.get(programme.id)?.add(programme.next_programme_id);
        adjacencyMap.get(programme.next_programme_id)?.add(programme.id);
    });

    const components: ProgrammeType[][] = [];
    const visited = new Set<number>();

    programmes.forEach((programme) => {
        if (visited.has(programme.id)) {
            return;
        }

        const queue = [programme.id];
        const component: ProgrammeType[] = [];
        visited.add(programme.id);

        while (queue.length > 0) {
            const currentId = queue.shift();

            if (currentId == null) {
                continue;
            }

            const currentProgramme = programmeMap.get(currentId);

            if (currentProgramme) {
                component.push(currentProgramme);
            }

            adjacencyMap.get(currentId)?.forEach((adjacentId) => {
                if (visited.has(adjacentId)) {
                    return;
                }

                visited.add(adjacentId);
                queue.push(adjacentId);
            });
        }

        components.push(sortProgrammes(component));
    });

    return components.sort(compareComponentsByLayout);
};

const orderComponentProgrammes = (
    programmes: ProgrammeType[],
    programmeMap: Map<number, ProgrammeType>,
) => {
    const incomingCounts = new Map(
        programmes.map((programme) => [programme.id, 0]),
    );
    const componentIds = new Set(programmes.map((programme) => programme.id));

    programmes.forEach((programme) => {
        if (
            programme.next_programme_id != null &&
            componentIds.has(programme.next_programme_id)
        ) {
            incomingCounts.set(
                programme.next_programme_id,
                (incomingCounts.get(programme.next_programme_id) ?? 0) + 1,
            );
        }
    });

    const entryProgrammes = sortProgrammes(
        programmes.filter(
            (programme) => (incomingCounts.get(programme.id) ?? 0) === 0,
        ),
    );
    const orderedProgrammes: ProgrammeType[] = [];
    const addedIds = new Set<number>();

    const addChain = (startingProgramme: ProgrammeType) => {
        let currentProgramme: ProgrammeType | undefined = startingProgramme;

        while (currentProgramme && !addedIds.has(currentProgramme.id)) {
            orderedProgrammes.push(currentProgramme);
            addedIds.add(currentProgramme.id);
            currentProgramme =
                currentProgramme.next_programme_id != null
                    ? programmeMap.get(currentProgramme.next_programme_id)
                    : undefined;

            if (currentProgramme && !componentIds.has(currentProgramme.id)) {
                currentProgramme = undefined;
            }
        }
    };

    entryProgrammes.forEach(addChain);
    sortProgrammes(programmes).forEach(addChain);

    return orderedProgrammes;
};

const sortProgrammes = (programmes: ProgrammeType[]) =>
    [...programmes].sort(compareProgrammesByLayout);

const compareComponentsByLayout = (
    left: ProgrammeType[],
    right: ProgrammeType[],
) => compareProgrammesByLayout(left[0], right[0]);

const compareProgrammesByLayout = (left: ProgrammeType, right: ProgrammeType) =>
    (left.year_id ?? Number.MAX_SAFE_INTEGER) -
        (right.year_id ?? Number.MAX_SAFE_INTEGER) ||
    left.title.localeCompare(right.title) ||
    left.id - right.id;

const getLiveProgrammeChains = (programmes: ProgrammeType[]) => {
    const programmeMap = new Map(
        programmes.map((programme) => [programme.id, programme]),
    );
    const includedIds = new Set<number>();

    programmes.forEach((programme) => {
        if (programme.status !== ProgrammeStatusType.LIVE) {
            return;
        }

        let currentProgramme: ProgrammeType | undefined = programme;

        while (currentProgramme && !includedIds.has(currentProgramme.id)) {
            includedIds.add(currentProgramme.id);
            currentProgramme =
                currentProgramme.next_programme_id != null
                    ? programmeMap.get(currentProgramme.next_programme_id)
                    : undefined;
        }
    });

    return programmes.filter((programme) => includedIds.has(programme.id));
};
